// src/components/personnes/PastillePersonne.tsx — SCE Issues
// La pastille d'une personne : ses INITIALES tout de suite, puis sa photo de
// profil quand elle arrive (Guillaume, 07/10 : rien ne doit s'allonger). Sans
// photo, ou si elle ne se lit pas, les initiales restent — sans message.
// Pointillée : une personne qu'on ne retrouve pas dans l'annuaire (nom repris
// de l'Excel, personne partie). Photo : api/annuaire (Graph, gardée pour la session).
//
// Partout dans l'appli depuis le 26/10 (présence, déclarant, compte, auteurs des
// photos, FNC-T) : chaque endroit garde l'apparence de SES initiales (`fond`,
// `couleur`, `style`) ; la photo, elle, n'a jamais de bordure ni d'anneau
// (Guillaume). Elle n'est pas prioritaire : photoDe attend que la page ait fini
// ses requêtes.

import { useEffect, useState, type CSSProperties, type JSX } from 'react'
import { photoDe } from '@/api/annuaire.api'
import { initialesDe } from '@/utils/noms'

/** La photo de profil d'une personne (adresse `blob:`), null tant qu'elle n'est pas là ou s'il n'y en a pas. */
export function usePhotoPersonne(upn: string | undefined): string | null {
  const [photo, setPhoto] = useState<string | null>(null)
  useEffect(() => {
    let vivante = true
    setPhoto(null)
    // Un invité (#EXT#) n'a pas de profil Sartorius : inutile de le demander.
    if (upn && !upn.includes('#EXT#')) void photoDe(upn).then((url) => vivante && setPhoto(url))
    return () => {
      vivante = false
    }
  }, [upn])
  return photo
}

/** L'image qui remplace les initiales, à poser dans une pastille ronde qui coupe ce qui dépasse. */
export function ImagePhoto({ src }: { src: string }): JSX.Element {
  return <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
}

export function PastillePersonne({
  nom,
  upn,
  taille = 32,
  pointillee = false,
  fond = 'var(--color-black)',
  couleur = 'var(--color-white)',
  taillePolice,
  style,
}: {
  nom: string
  /** Sans UPN, pas de photo : les initiales seules. */
  upn?: string
  taille?: number
  pointillee?: boolean
  /** Fond et texte des initiales (la photo les recouvre). */
  fond?: string
  couleur?: string
  taillePolice?: number
  /** Placement (marges, anneau d'une pile) ; bordure et anneau s'effacent sous la photo. */
  style?: CSSProperties
}): JSX.Element {
  const photo = usePhotoPersonne(upn)
  return (
    <span
      aria-hidden="true"
      style={{
        width: taille,
        height: taille,
        flexShrink: 0,
        borderRadius: taille / 2,
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: pointillee ? 'transparent' : fond,
        border: pointillee ? '1px dashed var(--color-text-secondary)' : 'none',
        color: pointillee ? 'var(--color-black)' : couleur,
        fontSize: taillePolice ?? Math.round(taille * 0.38),
        fontWeight: 700,
        ...style,
        ...(photo ? { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } : null),
      }}
    >
      {photo ? <ImagePhoto src={photo} /> : initialesDe(nom)}
    </span>
  )
}
